import { Component, h, Host, Prop, Element, ComponentInterface, Watch } from '@stencil/core';
import { IconSize } from '../sew-svg-icon/sew-icon.types';
import { E_SEW_BUTTON_SIZE, E_SEW_BUTTON_HIERARCHY } from './sew-button.types';
import { hasDensityMode } from '../../utils/hasDensityMode';

/**
 * SEW Button Component.
 */
@Component({
    tag: 'sew-button',
    styleUrl: 'sew-button.scss',
    shadow: true,
})
export class SewButton implements ComponentInterface {
    /**
     * State of button.
     */
    @Prop() hierarchy: E_SEW_BUTTON_HIERARCHY = E_SEW_BUTTON_HIERARCHY.PRIMARY;

    /**
     * If true, the button is monochrome.
     */
    @Prop() monochrome = false;

    /**
     * Size of button.
     */
    @Prop() size: E_SEW_BUTTON_SIZE = E_SEW_BUTTON_SIZE.S;

    /**
     * If provided, the specified icon is used with the sew-icon component.
     */
    @Prop() icon: string;

    /**
     * If true, the button is active and shows the corresponding animation.
     */
    @Prop({ reflect: true }) isActive: boolean;

    /**
     * If true, the button corresponding style changes
     */
    @Prop({ reflect: true }) selected: boolean;

    /**
     * Gets the host element.
     */
    @Element() public el: HTMLSewButtonElement;

    /**
     * If true, the button contains only an icon.
     */
    @Prop() showIconOnly: boolean;

    /**
     * Only needs to be set manually if the slotted Element is an <a> Element otherwise the <button> Element handles it.
     */
    @Prop({ reflect: true, mutable: true }) disabled: boolean;

    @Prop() suppressDensityMode = false;

    /**
     * Indicates a loading state
     */
    @Prop() loading: boolean;

    @Watch('loading')
    onLoading(): void {
        // TODO remove if this does not fix Giovannis problem
        this.slottedElement = this.el.querySelector('button, a') as HTMLButtonElement | HTMLAnchorElement;
        this.slottedElement.tagName === 'BUTTON' &&
            ((this.slottedElement as HTMLButtonElement).disabled = this.loading);

        this.slottedElement.tagName === 'A' && (this.disabled = this.loading);
    }

    /**
     * The slotted button or anchor.
     */
    private slottedElement: HTMLButtonElement | HTMLAnchorElement;

    private iconSizeMapping = {
        xxs: 's',
        xs: 'm',
        s: 'm',
        m: 'l',
    };

    /**
     * Mirrors the disabled state from the button to the host element.
     */
    private updateDisabledState = (): void => {
        if (this.slottedElement.tagName !== 'BUTTON') return;
        this.disabled = (this.slottedElement as HTMLButtonElement).disabled;
    };

    private disabledStateObserver = new MutationObserver(this.updateDisabledState);

    componentWillLoad(): void {
        this.initialize();
    }

    private initialize(): void {
        this.disabledStateObserver.disconnect();
        this.slottedElement = this.el.querySelector('button, a') as HTMLButtonElement | HTMLAnchorElement;
        this.slottedElement.tagName === 'BUTTON' &&
            this.loading &&
            ((this.slottedElement as HTMLButtonElement).disabled = true);
        this.slottedElement.tagName === 'A' && this.loading && (this.disabled = true);
        this.disabledStateObserver.observe(this.slottedElement, { attributeFilter: ['disabled'] });
        this.updateDisabledState();
        this.slottedElement.addEventListener('click', this.triggerActiveState);
    }

    public disconnectedCallback(): void {
        this.disabledStateObserver.disconnect();
    }

    private handleSlotChange = (): void => {
        this.initialize();
    };

    private triggerActiveState = (): void => {
        // don't show animation if button has icon only
        if (this.showIconOnly) return;

        this.isActive = true;
        setTimeout(() => {
            this.isActive = false;
        }, 1000);
    };

    render() {
        return (
            <Host
                class={{
                    [this.hierarchy]: true,
                    [this.size]: true,
                    'icon-only': this.showIconOnly,
                    'has-icon': !!this.icon || this.loading,
                    'active': this.isActive,
                    'loading': this.loading,
                    'as-button': this.slottedElement.tagName === 'BUTTON',
                    'monochrome': this.monochrome,
                    [hasDensityMode(this.el)]: !this.suppressDensityMode,
                }}
            >
                {this.icon && !this.loading && <sew-icon class="svg-icon" icon={this.icon} size="m" />}
                {this.loading && (
                    <sew-icon
                        class="svg-icon loading"
                        icon="circle-loader"
                        size={this.iconSizeMapping[this.size] as IconSize}
                    ></sew-icon>
                )}
                <slot onSlotchange={this.handleSlotChange} />
                <div class="active-animation"></div>
            </Host>
        );
    }
}
